import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { logout } from '../store/slices/authSlice'
import { useProfileRefresh } from '../hooks/useProfileRefresh'
import { useOwnerProfileSocket } from '../hooks/useOwnerProfileSocket'
import { can, isExecutive, isOwner } from '../utils/permissions'
import RulesModal from './RulesModal'
import MmCollapse from './MmCollapse'
import FixtureTicker from './FixtureTicker'
import fixturesService from '../services/fixturesService'
import { toTickerItem } from '../utils/fixtures'
import ChangePasswordModal from './ChangePasswordModal'
import OwnerAddBalanceModal from './OwnerAddBalanceModal'
import HeaderUserSearch from './HeaderUserSearch'
import MyMarketModal from './MyMarketModal'
import { buildSearchAnalysis } from '../data/searchAnalysis'
import { getMarketAnalysisDashboard, getSportsEvents } from '../services/api'
import LoaderOverlay from './LoaderOverlay'
import { getAlertCount } from '../services/systemTrackerService'
import { keysFor, shapeSportsTree, shapeSportsEvents } from '../utils/sportsTreeShape'
import { rememberLogout } from '../utils/reloginGuard'

// How long the page loader shows before the header's Rules modal opens
const RULES_LOADER_MS = 1000
// Full-screen loader before the header search's "My Market" modal opens
const MY_MARKET_LOADER_MS = 1000

function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.auth.user)
  const role = useSelector((state) => state.auth.role)

  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userDropdownOpen, setUserDropdownOpen] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [openSubmenus, setOpenSubmenus] = useState({})
  const [showRulesModal, setShowRulesModal] = useState(false)
  // Header "Rules" link: the reference shows the page loader (.loader-overlay
  // over .page-content) for a second and THEN opens the modal (user's
  // recording + DOM, 2026-09-18). That second is also used to re-read the
  // profile, so the modal opens on the DB's current `rules_accepted` (its
  // Accept row shows only while that is not true) — never on a stale copy.
  const [rulesLoading, setRulesLoading] = useState(false)
  const openRulesFromHeader = () => {
    if (rulesLoading || showRulesModal) return
    setRulesLoading(true)
    const minWait = new Promise((r) => setTimeout(r, RULES_LOADER_MS))
    Promise.all([minWait, refreshProfile()]).finally(() => {
      setRulesLoading(false)
      setShowRulesModal(true)
    })
  }

  // Header "Search User" pick → the reference's "My Market" modal
  // (report/searchanalysis): full-screen loader for a second while that
  // account's market book loads (dashboard API scoped by username), then the
  // modal slides in. Closes the mobile magnifier dropdown too.
  const [myMarket, setMyMarket] = useState({ show: false, analysis: null, matches: [] })
  const [myMarketLoading, setMyMarketLoading] = useState(false)
  const openSearchedUser = (row) => {
    setMobileSearchOpen(false)
    if (myMarketLoading) return
    setMyMarketLoading(true)
    const minWait = new Promise((r) => setTimeout(r, MY_MARKET_LOADER_MS))
    const load = getMarketAnalysisDashboard({ username: row.username })
      .then((res) => (res?.success ? res.data || [] : []))
      .catch(() => [])
    Promise.all([minWait, load]).then(([, matches]) => {
      setMyMarketLoading(false)
      // Game Detail + direct parent come from the search row (data/searchAnalysis);
      // BACKEND TODO: swap for the report/searchanalysis response when it ships
      setMyMarket({ show: true, analysis: buildSearchAnalysis(row), matches })
    })
  }
  // true while the rules modal is open because WE opened it (flag false), as
  // opposed to the operator opening it from the header link
  const rulesAutoOpenRef = useRef(false)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [showAddBalance, setShowAddBalance] = useState(false)
  const [sportsTree, setSportsTree] = useState([])
  const [openSports, setOpenSports] = useState({})
  // System Tracker bell — owner only. Cheap cached read (no scan), so a slow poll
  // is enough; the background worker is what keeps the numbers fresh.
  const [trackerAlerts, setTrackerAlerts] = useState(0)

  // BootstrapVue dropdowns position their menu with Popper — replicate the
  // same open-state DOM: root .show, x-placement + translate3d inline style
  const userBtnRef = useRef(null)
  const userMenuRef = useRef(null)
  const [userMenuStyle, setUserMenuStyle] = useState(null)
  const searchBtnRef = useRef(null)
  const searchMenuRef = useRef(null)
  const [searchMenuStyle, setSearchMenuStyle] = useState(null)

  const popperMenuStyle = (btn, menu) => {
    const parent = menu.offsetParent || menu.parentElement
    const p = parent.getBoundingClientRect()
    const b = btn.getBoundingClientRect()
    const mw = menu.offsetWidth
    let left = b.right - mw // bottom-end: right edges aligned
    const maxLeft = window.innerWidth - 5 - mw
    if (left > maxLeft) left = maxLeft
    if (left < 5) left = 5 // viewport overflow guard (Popper padding, same as reference)
    const x = Math.round(left - p.left)
    const y = Math.round(b.bottom - p.top)
    return { position: 'absolute', transform: `translate3d(${x}px, ${y}px, 0px)`, top: '0px', left: '0px', willChange: 'transform' }
  }

  // Har route change (aur same-route re-click) pe page hamesha top se khule.
  // instant (behavior:'instant') — html me `scroll-behavior:smooth` hai warna
  // visible animate hota hai.
  useEffect(() => {
    const toTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0
      if (document.documentElement) document.documentElement.scrollTop = 0
      if (document.body) document.body.scrollTop = 0
      document.querySelectorAll('.main-content, .page-content').forEach((el) => { el.scrollTop = 0 })
    }
    toTop()
    const raf = requestAnimationFrame(toTop)
    return () => cancelAnimationFrame(raf)
  }, [location.key])

  useEffect(() => {
    if (userDropdownOpen && userBtnRef.current && userMenuRef.current) {
      setUserMenuStyle(popperMenuStyle(userBtnRef.current, userMenuRef.current))
    } else {
      setUserMenuStyle(null)
    }
  }, [userDropdownOpen])

  useEffect(() => {
    if (mobileSearchOpen && searchBtnRef.current && searchMenuRef.current) {
      setSearchMenuStyle(popperMenuStyle(searchBtnRef.current, searchMenuRef.current))
    } else {
      setSearchMenuStyle(null)
    }
  }, [mobileSearchOpen])

  // Close the header dropdowns on any outside click (BootstrapVue behavior)
  useEffect(() => {
    if (!userDropdownOpen && !mobileSearchOpen) return
    const onDown = (e) => {
      if (e.target.closest && (e.target.closest('#__BVID__21') || e.target.closest('#__BVID__17'))) return
      setUserDropdownOpen(false)
      setMobileSearchOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [userDropdownOpen, mobileSearchOpen])

  useEffect(() => {
    if (!user) return
    // Executives don't carry rules_accepted and can't call the accept-rules
    // API (it needs an owner/staff id) — never show them the rules modal
    if (role === 'executive') return
    if (user.rules_accepted === false) {
      // Backend flag from the profile API: keep showing until the user accepts
      rulesAutoOpenRef.current = true
      setShowRulesModal(true)
    } else if (user.rules_accepted === true && rulesAutoOpenRef.current) {
      // The flag turned true underneath an auto-opened modal — a profile
      // refresh / socket push / login race said "accepted" after a stale
      // `false` had opened it (saluagent, 2026-09-18: profile true, modal
      // still up). Nothing left to accept: take it down. A modal the operator
      // opened from the header link is not ours to close.
      rulesAutoOpenRef.current = false
      setShowRulesModal(false)
    } else if (user.rules_accepted === undefined && !sessionStorage.getItem('rulesModalShown')) {
      // Fallback for a backend without the flag yet: once per session
      setShowRulesModal(true)
      sessionStorage.setItem('rulesModalShown', 'true')
    }
  }, [user])

  // Refresh the logged-in account's profile/wallet on every page change. The
  // role rules (owner vs staff endpoint, executive mirroring its parent) live
  // in useProfileRefresh, which the credit deposit/withdraw flow also calls —
  // a route change is not the only moment this balance goes stale.
  const refreshProfile = useProfileRefresh()
  useEffect(() => {
    refreshProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // Owner/executive sessions additionally get the profile PUSHED every 2s over
  // Socket.IO, so the header and the dashboard cards stay live without a route
  // change. Layout wraps every admin page, so subscribing once here covers all
  // of them. The route-change refresh above stays as the staff path + cold-start
  // fallback.
  useOwnerProfileSocket()

  /* The sidebar tree is POLLED, not fetched once. The upstream feed adds and
     drops competitions all day — a new series appeared mid-session and landed
     at index 16, not at the end — so a one-shot fetch goes stale within
     minutes. 3s matches the server's own cache on /user/all/sports/tree
     (CACHE_TTL = 2s in CricketService), so the upstream provider is never hit
     more often than it already is. Two guards keep the poll invisible:
     `treeInFlightRef` drops a tick while the previous request is still open,
     and the signature check below skips setState when the payload has not
     actually changed — without it every tick would re-render the whole tree
     and fight the open accordions. */
  const treeSigRef = useRef('')
  const seriesOrderRef = useRef(new Map())
  const treeInFlightRef = useRef(false)

  useEffect(() => {
    let alive = true
    const load = async () => {
      if (treeInFlightRef.current) return
      treeInFlightRef.current = true
      try {
        /* /user/all/sports/events hands the tree back already ordered —
           sports in the feed's sequence with racing after Tennis, leagues by
           their soonest match — so the client only regroups racing. The
           fallback is the old tree endpoint + the held seed order, for an
           admin that is newer than its server. */
        const res = await getSportsEvents()
        let next
        if (res.ordered) {
          next = shapeSportsEvents(res.sports)
        } else {
          let node = res.legacy
          while (node && node.data && !node.t1 && !node.t2) node = node.data
          next = shapeSportsTree(node?.t1 || [], node?.t2 || [], seriesOrderRef.current)
        }
        const sig = JSON.stringify(next)
        if (!alive || sig === treeSigRef.current) return
        treeSigRef.current = sig
        setSportsTree(next)
      } catch (err) {
        console.error('Error loading sports tree:', err)
      } finally {
        treeInFlightRef.current = false
      }
    }
    load()
    /* 3s auto-refetch — PAUSED on request (user, 2026-09-15). The loader above
       is unchanged and still guarded (treeInFlightRef + the signature check),
       so re-enabling is just uncommenting these two lines. Measured while it
       ran: request gaps 3000 / 3000 / 2999 / 3002 ms, open accordions survived
       every tick. While it is off the tree is fetched once per mount, as it
       was before. */
    // const id = setInterval(load, 3000)
    // return () => { alive = false; clearInterval(id); treeInFlightRef.current = false }
    /* The in-flight flag MUST be cleared on unmount. StrictMode mounts, unmounts
       and remounts this effect in dev: mount 1 starts the request and sets the
       flag, its cleanup marks that closure dead, then mount 2 hits the flag and
       returns without fetching — and when request 1 lands its own `alive` is
       already false, so nothing is ever stored and the Events tree renders
       empty. While the 3s poll was running a later tick covered it up; with the
       poll off there is no second chance. */
    return () => { alive = false; treeInFlightRef.current = false }
  }, [])

  // Upcoming fixtures for the header ticker — ONLY what the fixtures collector
  // stored (auto-update/tenexch, every 4 h), not the sidebar sports tree.
  const [upcomingFixtures, setUpcomingFixtures] = useState([])
  useEffect(() => {
    let alive = true
    const load = () =>
      fixturesService.get()
        .then((res) => { if (alive) setUpcomingFixtures((res.data || []).map(toTickerItem)) })
        .catch(() => {}) // keep the last list; the ticker just stays as it was
    load()
    const t = setInterval(load, 5 * 60 * 1000)
    return () => { alive = false; clearInterval(t) }
  }, [])

  /* One open row per level, metisMenu's `toggle: true` — the reference closes the
     sport you had open the moment you open another one (user, 2026-09-15: "Football
     pe click kiya, andar child khola, phir doosra khola to Football wala close ho
     gaya"). Ours kept every branch you had ever opened, so the tree grew into a wall.
     Keys carry their level: `sport-<etid>` and `ser-<etid>-<index>-<cid>`, so opening
     a sport drops every other sport AND every series (they hang off the sport that
     just closed), and opening a series drops only its own siblings — the series of
     the same sport. Closing a row touches nothing else. */
  const toggleSport = useCallback((key) => {
    setOpenSports((prev) => {
      if (prev[key]) return { ...prev, [key]: false }          // closing: leave the rest alone
      if (key.startsWith('sport-')) return { [key]: true }     // a sport: sole survivor
      const sportPrefix = `ser-${key.split('-')[1]}-`          // a series: keep other sports' state
      const next = {}
      Object.keys(prev).forEach((k) => { if (prev[k] && !k.startsWith(sportPrefix)) next[k] = true })
      next[key] = true
      return next
    })
  }, [])

  /* The match the page is showing, straight from the URL
     (/admin/game-details/:etid/:gmid). The reference keeps the same two ids in
     its store — the game-details page writes `state.etid` / `state.cid` /
     `state.gameId` from `gameDetail` — and the sidebar reads them:

       treeData(state) {                                  // store getter
         for (sport of state.tree) {
           sport.expand = state.etid == sport.etid
           for (league of sport.children) league.expand = state.cid == league.cid
         }
       }
       <li :class="{show: item.expand}">
         <a :class="[…, item.cid == state.cid ? 'active' : '']">      // league
         <router-link :class="item.gmid == state.gameId ? 'active' : ''">  // match
         <ul class="sub-menu mm-collapse" :class="{'mm-show': item.expand}">

     so opening a match lights its sport, league and row and unfolds the branch
     — the orange rows in the user's recording (2026-09-22). `cid` is not in
     our URL, but it does not need to be: the league is the one in the tree
     that holds this gmid. */
  const gameRoute = location.pathname.match(/^\/admin\/game-details\/(\d+)\/(\d+)/)
  const activeEtid = gameRoute?.[1] || null
  const activeGmid = gameRoute?.[2] || null
  const activeSportKey = activeEtid ? `sport-${activeEtid}` : null
  const activeSerKey = useMemo(() => {
    if (!activeEtid || !activeGmid) return null
    const sport = sportsTree.find((s) => String(s.etid) === activeEtid)
    const series = sport?.children || []
    const idx = series.findIndex((ser) => (ser.children || []).some((m) => String(m.gmid) === activeGmid))
    return idx >= 0 ? `ser-${activeEtid}-${keysFor(series)[idx]}` : null
  }, [sportsTree, activeEtid, activeGmid])

  /* Unfold that branch when the match changes — once, so the operator can
     still collapse it by hand (the reference recomputes `expand` on every
     store read and would fight them; that is the one place we differ). */
  const unfoldedFor = useRef(null)
  useEffect(() => {
    if (!activeSportKey || !activeSerKey) return
    const stamp = `${activeSportKey}|${activeSerKey}`
    if (unfoldedFor.current === stamp) return
    unfoldedFor.current = stamp
    setOpenSubmenus((prev) => ({ ...prev, events: true }))
    setOpenSports({ [activeSportKey]: true, [activeSerKey]: true })
  }, [activeSportKey, activeSerKey])

  // Auto-close the mobile slide-in sidebar after navigating from a menu item
  // (same as the reference: the menu closes as soon as you go inside a page).
  useEffect(() => {
    if (window.innerWidth < 992) {
      document.body.classList.remove('sidebar-enable')
      setSidebarOpen(false)
    }
  }, [location.pathname])

  const toggleSidebar = () => {
    // Same behavior as the reference (Skote-style): mobile gets only
    // `sidebar-enable` (slide-in menu); desktop also toggles the collapsed
    // icon-only mode via `vertical-collpsed`.
    document.body.classList.toggle('sidebar-enable')
    if (window.innerWidth >= 992) {
      document.body.classList.toggle('vertical-collpsed')
    } else {
      document.body.classList.remove('vertical-collpsed')
    }
    setSidebarOpen((prev) => !prev)
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
    } else {
      document.exitFullscreen()
    }
  }

  // Poll the tracker alert count for the header bell. Owner-only: staff and
  // executives get 403 from the route, so don't even ask.
  useEffect(() => {
    if (!isOwner()) return undefined
    let cancelled = false
    const tick = async () => {
      try {
        const res = await getAlertCount()
        // actionable, not totalIssues — informational rows must not keep the badge lit
        if (!cancelled) setTrackerAlerts(Number(res?.actionable ?? res?.totalIssues ?? 0))
      } catch {
        // a failed poll must never break the shell the whole panel renders inside
      }
    }
    tick()
    const id = setInterval(tick, 120000)
    return () => { cancelled = true; clearInterval(id) }
  }, [])

  // Same one-at-a-time rule for the top-level drawers (Account / Reports /
  // Settlement / Events) — metisMenu applies `toggle` at every level.
  const toggleSubmenu = (key) => {
    setOpenSubmenus((prev) => (prev[key] ? { ...prev, [key]: false } : { [key]: true }))
  }

  const handleLogout = () => {
    // Arms the re-login rule (utils/reloginGuard.js): after THIS logout every
    // login attempt is refused until the page is reloaded.
    rememberLogout()
    dispatch(logout())
    navigate('/admin')
  }

  return (
    <>
      <div id="layout-wrapper" className="point-inner">
        {/* HEADER */}
        <header id="page-topbar">
          <div className="navbar-header">
            <div className="d-flex">
              {/* Logo */}
              <div className="navbar-brand-box">
                <Link to="/admin/home" className="logo logo-light">
                  <span className="logo-sm">
                    <img src="/admin/assets/admin/icon.png" alt="" height="22" />
                  </span>
                  <span className="logo-lg">
                    <img src="/admin/assets/front/logo.png" alt="" className="site-logo" />
                  </span>
                </Link>
              </div>

              {/* Sidebar toggle */}
              <button id="vertical-menu-btn" type="button" className="btn btn-sm px-3 font-size-16 header-item" onClick={toggleSidebar}>
                <i className="fa fa-fw fa-bars"></i>
              </button>

              {/* Search User - desktop (components/HeaderUserSearch) */}
              <div className="site-searchbox mt-3 d-none d-lg-inline-block">
                <HeaderUserSearch onSelect={openSearchedUser} />
              </div>
            </div>

            {/* Upcoming Fixtures */}
            <div className="w-100">
              <div className="upcoming-fixure fixture-nav">
                <div className="fixure-title">Upcoming Fixtures</div>
                <div className="fixure-box-container1">
                  <FixtureTicker fixtures={upcomingFixtures} />
                </div>
              </div>
            </div>

            {/* Right side */}
            <div className="d-flex">
              {/* Mobile search dropdown */}
              <div className={`dropdown b-dropdown d-inline-block d-lg-none ml-2${mobileSearchOpen ? ' show' : ''} btn-group`} id="__BVID__17">
                <button
                  ref={searchBtnRef}
                  aria-haspopup="menu"
                  aria-expanded={mobileSearchOpen ? 'true' : 'false'}
                  type="button"
                  className="btn dropdown-toggle btn-black header-item noti-icon"
                  id="__BVID__17__BV_toggle_"
                  onClick={() => { setMobileSearchOpen((prev) => !prev); setUserDropdownOpen(false) }}
                >
                  <i className="mdi mdi-magnify"></i>
                </button>
                <ul ref={searchMenuRef} role="menu" tabIndex="-1" className={`dropdown-menu dropdown-menu-lg p-0 dropdown-menu-right${mobileSearchOpen ? ' show' : ''}`} aria-labelledby="__BVID__17__BV_toggle_" {...(mobileSearchOpen ? { 'x-placement': 'bottom-end' } : {})} style={searchMenuStyle || undefined}>
                  {' '}
                  <form className="p-3">
                    <div className="form-group m-0">
                      <div className="input-group">
                        <HeaderUserSearch onSelect={openSearchedUser} active={mobileSearchOpen} />
                      </div>
                    </div>
                  </form>
                </ul>
              </div>

              {/* System Tracker bell — owner only */}
              {isOwner() && (
                <div className="dropdown d-none d-sm-inline-block ml-1">
                  <Link
                    to="/admin/system-tracker"
                    className="btn header-item noti-icon position-relative"
                    title={trackerAlerts > 0
                      ? `${trackerAlerts} integrity finding(s) — open System Tracker`
                      : 'System Tracker — no open findings'}
                  >
                    <i className="bx bx-shield-quarter"></i>
                    {trackerAlerts > 0 && (
                      <span className="badge bg-danger position-absolute" style={{ top: 8, right: 4, fontSize: 10 }}>
                        {trackerAlerts > 99 ? '99+' : trackerAlerts}
                      </span>
                    )}
                  </Link>
                </div>
              )}

              {/* Fullscreen */}
              <div className="dropdown d-none d-lg-inline-block ml-1">
                <button type="button" className="btn header-item noti-icon" onClick={toggleFullscreen}>
                  <i className="bx bx-fullscreen"></i>
                </button>
              </div>

              {/* Rules */}
              <div className="d-none d-sm-inline-block rules-icon nowrap">
                <span className="main-rules">
                  <a href="javascript:void(0)" onClick={openRulesFromHeader}>
                    <i className="fas fa-info-circle mr-1"></i>Rules
                  </a>
                </span>
              </div>

              {/* Balance */}
              <div className="dropdown d-none d-sm-inline-block ml-1">
                <button type="button" className="btn header-item noti-icon">
                  <span className="balance nowrap">pts: <span className="balance-value"><b>{Number(user?.inr_balance ?? user?.balance ?? user?.AvailableBalance ?? 0).toLocaleString()}</b></span>
                    {/* API sends a positive payout risk; shown as a loss (-), hidden when 0 */}
                    {Number(user?.total_exposure) ? (
                      <> | <span className="balance-value">{(-Math.abs(Number(user.total_exposure))).toLocaleString()}</span></>
                    ) : null}
                  </span>
                </button>
              </div>

              {/* User Dropdown */}
              <div className={`dropdown b-dropdown${userDropdownOpen ? ' show' : ''} btn-group`} id="__BVID__21">
                <button
                  ref={userBtnRef}
                  aria-haspopup="menu"
                  aria-expanded={userDropdownOpen ? 'true' : 'false'}
                  type="button"
                  className="btn dropdown-toggle btn-black header-item"
                  id="__BVID__21__BV_toggle_"
                  onClick={() => { setUserDropdownOpen((prev) => !prev); setMobileSearchOpen(false) }}
                >
                  <span className="ml-1">{user?.username || ''}</span>{' '}
                  <i className="mdi mdi-chevron-down"></i>
                </button>
                <ul ref={userMenuRef} role="menu" tabIndex="-1" className={`dropdown-menu dropdown-menu-right${userDropdownOpen ? ' show' : ''}`} aria-labelledby="__BVID__21__BV_toggle_" {...(userDropdownOpen ? { 'x-placement': 'bottom-end' } : {})} style={userMenuStyle || undefined}>
                  <div className="dropdown d-sm-none ml-1 mr-1">
                    <div className="bal-box">
                      <span className="balance nowrap">pts: <span className="balance-value"><b>{Number(user?.inr_balance ?? user?.balance ?? user?.AvailableBalance ?? 0).toLocaleString()}</b></span>
                        {Number(user?.total_exposure) ? (
                          <> | <span className="balance-value">{(-Math.abs(Number(user.total_exposure))).toLocaleString()}</span></>
                        ) : null}
                      </span>
                    </div>
                  </div>
                  <a href="javascript:void(0)" className="dropdown-item d-sm-none" onClick={openRulesFromHeader}>
                    <i className="fas fa-info-circle mr-1"></i> Rules
                  </a>
                  {/* Secure Auth (2FA) and self password change hit owner/staff
                      endpoints an executive doesn't have — hide both for them */}
                  {!isExecutive() && (
                  // Was a <Link> nested inside an <a href="#"> — the outer
                  // anchor swallowed the click, so the route never fired.
                  // Plain Link now, same shape as the siblings, and it closes
                  // the dropdown like Change Password does.
                  <Link
                    to="/admin/secureauth"
                    className="dropdown-item"
                    onClick={() => setUserDropdownOpen(false)}
                  >
                    <i className="bx bx-lock-open font-size-16 align-middle mr-1"></i> Secure Auth
                  </Link>
                  )}
                  {/* Owner Add Balance — owner apne hi wallet me; staff/executive ko nahi dikhta */}
                  {isOwner() && (
                  <a href="javascript:void(0)" className="dropdown-item" onClick={() => { setShowAddBalance(true); setUserDropdownOpen(false) }}>
                    <i className="bx bx-plus-circle font-size-16 align-middle mr-1"></i> Add Balance
                  </a>
                  )}
                  {!isExecutive() && (
                  <a href="javascript:void(0)" className="dropdown-item" onClick={() => { setShowChangePassword(true); setUserDropdownOpen(false) }}>
                    <i className="bx bx-wallet font-size-16 align-middle mr-1"></i> Change Password
                  </a>
                  )}
                  <div className="dropdown-divider"></div>
                  <a href="javascript:void(0)" className="dropdown-item text-danger" onClick={handleLogout}>
                    <i className="bx bx-power-off font-size-16 align-middle mr-1 text-danger"></i> Logout
                  </a>
                </ul>
              </div>
            </div>
          </div>
        </header>

        {/* SIDEBAR */}
        <div className="vertical-menu">
          <div className="h-100" data-simplebar="init">
            <div className="simplebar-wrapper" style={{ margin: 0 }}>
              <div className="simplebar-height-auto-observer-wrapper">
                <div className="simplebar-height-auto-observer"></div>
              </div>
              <div className="simplebar-mask">
                <div className="simplebar-offset" style={{ right: 0, bottom: 0 }}>
                  <div className="simplebar-content-wrapper" tabIndex="0" role="region" aria-label="scrollable content" style={{ height: '100%', overflow: 'hidden scroll' }}>
                    <div className="simplebar-content" style={{ padding: 0 }}>
                      <div id="sidebar-menu">
                        <ul id="side-menu" className="metismenu list-unstyled">
                          {/* Menu hiding is cosmetic — ProtectedRoute bounces direct
                              URLs and requirePermission 403s the API. Settlement,
                              carousel and game priority are owner-only (isOwner):
                              hidden from staff and executives alike. */}
                          {can('dashboard') && (
                          <li className={location.pathname === '/admin/home' ? 'mm-active' : ''}>
                            <Link to="/admin/home" className={`side-nav-link-ref${location.pathname === '/admin/home' ? ' active' : ''}`}>
                              <i className="bx bx-home-circle"></i> <span>Dashboard</span>
                            </Link>
                          </li>
                          )}
                          {can('marketAnalysis') && (
                          <li className={location.pathname === '/admin/market-analysis' ? 'mm-active' : ''}>
                            <Link to="/admin/market-analysis" className="side-nav-link-ref">
                              <i className="bx bxs-bar-chart-alt-2"></i> <span>Market Analysis</span>
                            </Link>
                          </li>
                          )}
                          {can('loginUserCreation') && (
                          <li>
                            <Link to="/admin/createaccount" className="side-nav-link-ref">
                              <i className="bx bx-user-plus"></i> <span>Multi Login Account</span>
                            </Link>
                          </li>
                          )}
                          {/* Account submenu — parent shows when ANY child is granted */}
                          {can(['activeUser', 'userList', 'insertUser']) && (
                          <li className={openSubmenus.account ? 'mm-active' : ''}>
                            <a href="javascript:void(0);" className="has-arrow" aria-expanded={openSubmenus.account ? 'true' : 'false'} onClick={() => toggleSubmenu('account')}>
                              <i className="bx bx-user-circle"></i> <span>Account</span>
                            </a>
                            <MmCollapse aria-expanded="false" open={!!openSubmenus.account} className="sub-menu">
                              {can('activeUser') && <li><Link to="/admin/activeusers" className="side-nav-link-ref">Account List For Active Users</Link></li>}
                              {can('userList') && <li><Link to="/admin/users" className="side-nav-link-ref">Account List</Link></li>}
                              {can('insertUser') && <li><Link to="/admin/users/insertuser" className="side-nav-link-ref">Create Account</Link></li>}
                            </MmCollapse>
                          </li>
                          )}
                          {can('agentAssign') && (
                          <li>
                            <Link to="/admin/assign-agent" className="side-nav-link-ref">
                              <i className="bx bx-user"></i> <span>Assign Agent</span>
                            </Link>
                          </li>
                          )}
                          {can('bank') && (
                          <li>
                            <Link to="/admin/reports/bank" className="side-nav-link-ref">
                              <i className="bx bxs-bank"></i> <span>Bank</span>
                            </Link>
                          </li>
                          )}
                          {isOwner() && (
                          <li className={location.pathname === '/admin/system-tracker' ? 'mm-active' : ''}>
                            <Link to="/admin/system-tracker" className={`side-nav-link-ref${location.pathname === '/admin/system-tracker' ? ' active' : ''}`}>
                              <i className="bx bx-shield-quarter"></i> <span>System Tracker</span>
                              {trackerAlerts > 0 && <span className="badge bg-danger ms-2">{trackerAlerts}</span>}
                            </Link>
                          </li>
                          )}
                          {isOwner() && (
                          <li>
                            <Link to="/admin/carousel" className={`side-nav-link-ref${location.pathname === '/admin/carousel' ? ' active' : ''}`}>
                              <i className="bx bx-images"></i> <span>Carousel</span>
                            </Link>
                          </li>
                          )}
                          {/* Fixtures — owner only: what the fixtures collector stored
                              and when (the same list drives the header ticker). */}
                          {isOwner() && (
                          <li>
                            <Link to="/admin/fixtures" className={`side-nav-link-ref${location.pathname === '/admin/fixtures' ? ' active' : ''}`}>
                              <i className="bx bx-calendar"></i> <span>Fixtures</span>
                            </Link>
                          </li>
                          )}
                          {/* Home Sliders — owner only: the "Our Casino" and
                              "New Games" image strips on the mobile home page. */}
                          {isOwner() && (
                          <li>
                            <Link to="/admin/home-slider" className={`side-nav-link-ref${location.pathname === '/admin/home-slider' ? ' active' : ''}`}>
                              <i className="bx bx-slideshow"></i> <span>Home Sliders</span>
                            </Link>
                          </li>
                          )}
                          {isOwner() && (
                          <li>
                            <Link to="/admin/game-priority" className={`side-nav-link-ref${location.pathname === '/admin/game-priority' ? ' active' : ''}`}>
                              <i className="bx bx-sort-up"></i> <span>Game Priority</span>
                            </Link>
                          </li>
                          )}
                          {isOwner() && (
                          <li>
                            <Link to="/admin/settings/secureauthbot" className={`side-nav-link-ref${location.pathname === '/admin/settings/secureauthbot' ? ' active' : ''}`}>
                              <i className="bx bx-shield-quarter"></i> <span>Secure Auth Bot</span>
                            </Link>
                          </li>
                          )}
                          {/* Reports submenu — parent shows when ANY child is granted */}
                          {can(['accountStatement', 'partyWinLoss', 'currentBets', 'userHistory', 'generalLock', 'casinoResult', 'liveCasinoResult', 'turnOver', 'fraudReport', 'userRegisterReport', 'totalProfitLoss', 'userWinLoss']) && (
                          <li className={openSubmenus.reports ? 'mm-active' : ''}>
                            <a href="javascript:void(0);" className="has-arrow" aria-expanded={openSubmenus.reports ? 'true' : 'false'} onClick={() => toggleSubmenu('reports')}>
                              <i className="bx bx-file"></i> <span>Reports</span>
                            </a>
                            <MmCollapse aria-expanded="false" open={!!openSubmenus.reports} className="sub-menu">
                              {can('accountStatement') && <li><Link to="/admin/reports/accountstatement" className="side-nav-link-ref">Account Statement</Link></li>}
                              {can('partyWinLoss') && <li><Link to="/admin/reports/profitloss" className="side-nav-link-ref">Party Win Loss</Link></li>}
                              {can('currentBets') && <li><Link to="/admin/reports/currentbets" className="side-nav-link-ref">Current Bets</Link></li>}
                              {can('userHistory') && <li><Link to="/admin/reports/userhistory" className="side-nav-link-ref">User History</Link></li>}
                              {can('generalLock') && <li><Link to="/admin/settings/userlock" className="side-nav-link-ref">General Lock</Link></li>}
                              {can('casinoResult') && <li><Link to="/admin/reports/casinoresult" className="side-nav-link-ref">Our Casino Result</Link></li>}
                              {can('liveCasinoResult') && <li><Link to="/admin/reports/livecasinoreport" className="side-nav-link-ref">Live Casino Result</Link></li>}
                              {!isExecutive() && <li><Link to="/admin/reports/sportbookreport" className="side-nav-link-ref">Sportbook Report</Link></li>}
                              {can('turnOver') && <li><Link to="/admin/reports/turnover" className="side-nav-link-ref">Turn Over</Link></li>}
                              {!isExecutive() && <li><Link to="/admin/reports/authlist" className="side-nav-link-ref">User Authentication</Link></li>}
                              {can('fraudReport') && <li><Link to="/admin/reports/fraud" className="side-nav-link-ref">Fraud Report</Link></li>}
                              {can('userRegisterReport') && <li><Link to="/admin/reports/userregisterdetail" className="side-nav-link-ref">User Register Detail</Link></li>}
                              {can('totalProfitLoss') && <li><Link to="/admin/reports/totalprofitloss" className="side-nav-link-ref">Total Profit Loss</Link></li>}
                              {can('userWinLoss') && <li><Link to="/admin/reports/userwinloss" className="side-nav-link-ref">User Win Loss</Link></li>}
                            </MmCollapse>
                          </li>
                          )}
                          {/* Settlement — owner-only: never shown to staff or executives */}
                          {isOwner() && (
                          <li className={openSubmenus.settlement ? 'mm-active' : ''}>
                            <a href="javascript:void(0);" className="has-arrow" aria-expanded={openSubmenus.settlement ? 'true' : 'false'} onClick={() => toggleSubmenu('settlement')}>
                              <i className="bx bx-check-shield"></i> <span>Settlement</span>
                            </a>
                            <MmCollapse aria-expanded="false" open={!!openSubmenus.settlement} className="sub-menu">
                              <li><Link to="/admin/settlement/match" className="side-nav-link-ref">Match Settlement</Link></li>
                              <li><Link to="/admin/settlement/fancy" className="side-nav-link-ref">Fancy Settlement</Link></li>
                              <li><Link to="/admin/settlement/void-bets" className="side-nav-link-ref">Void Bets</Link></li>
                              <li><Link to="/admin/settlement/casino" className="side-nav-link-ref">Casino Settlement</Link></li>
                            </MmCollapse>
                          </li>
                          )}
                          {can('ourCasino') && (
                          <li>
                            <Link to="/admin/casino/list" className="side-nav-link-ref">
                              <i className="mdi mdi-cards-playing-outline"></i> <span>Our Casino</span>
                            </Link>
                          </li>
                          )}
                          {can('ourCasino') && (
                          <li>
                            <Link to="/admin/casino/vip" className="side-nav-link-ref">
                              <span className="badge badge-pill badge-success float-right">New</span>
                              <i className="mdi mdi-cards-playing-outline"></i> <span>Vip Casino</span>
                            </Link>
                          </li>
                          )}
                          {can('ourCasino') && (
                          <li>
                            <Link to="/admin/vcasino/list" className="side-nav-link-ref">
                              <span className="badge badge-pill badge-success float-right">New</span>
                              <i className="mdi mdi-cards-playing-outline"></i> <span>Virtual Casino</span>
                            </Link>
                          </li>
                          )}
                          {can('ourCasino') && (
                          <li>
                            <Link to="/admin/pcasino/list" className="side-nav-link-ref">
                              <span className="badge badge-pill badge-success float-right">New</span>
                              <i className="mdi mdi-cards-playing-outline"></i> <span>Premium Casino</span>
                            </Link>
                          </li>
                          )}
                          {can('ourCasino') && (
                          <li>
                            <Link to="/admin/tcasino/list" className="side-nav-link-ref">
                              <span className="badge badge-pill badge-success float-right">New</span>
                              <i className="mdi mdi-cards-playing-outline"></i> <span>Tembo Casino</span>
                            </Link>
                          </li>
                          )}
                          {/* Casino Manage — owner only: enable/disable the
                              VIP/Virtual/Premium tables and set their entry
                              thresholds. Executives/staff never see it. */}
                          {isOwner() && (
                          <li className={location.pathname === '/admin/casino-manage' ? 'mm-active' : ''}>
                            <Link to="/admin/casino-manage" className={`side-nav-link-ref${location.pathname === '/admin/casino-manage' ? ' active' : ''}`}>
                              <i className="mdi mdi-cog-outline"></i> <span>Casino Manage</span>
                            </Link>
                          </li>
                          )}
                          {/* Buffer Time — owner only: bet-placement buffer per
                              sport x market category (Match Odds / Bookmaker /
                              Fancy) and per casino game. */}
                          {isOwner() && (
                          <li className={location.pathname === '/admin/settings/buffer-time' ? 'mm-active' : ''}>
                            <Link to="/admin/settings/buffer-time" className={`side-nav-link-ref${location.pathname === '/admin/settings/buffer-time' ? ' active' : ''}`}>
                              <i className="mdi mdi-timer-outline"></i> <span>Buffer Time</span>
                            </Link>
                          </li>
                          )}
                          {/* Commission — owner only: % of a user's net win taken
                              per sport x market type, shown as "- Comm" in the
                              statement. */}
                          {isOwner() && (
                          <li className={location.pathname === '/admin/settings/commission' ? 'mm-active' : ''}>
                            <Link to="/admin/settings/commission" className={`side-nav-link-ref${location.pathname === '/admin/settings/commission' ? ' active' : ''}`}>
                              <i className="mdi mdi-percent"></i> <span>Commission</span>
                            </Link>
                          </li>
                          )}
                          {/* Max Odds — owner only: highest rate a bet may be
                              placed at, per sport x Match Odds / Bookmaker
                              (cricket cells can be Oneday & T20 only). */}
                          {isOwner() && (
                          <li className={location.pathname === '/admin/settings/max-odds' ? 'mm-active' : ''}>
                            <Link to="/admin/settings/max-odds" className={`side-nav-link-ref${location.pathname === '/admin/settings/max-odds' ? ' active' : ''}`}>
                              <i className="mdi mdi-speedometer"></i> <span>Max Odds</span>
                            </Link>
                          </li>
                          )}
                          {/* Auto Void — owner only: void still-open undeclared
                              bets N seconds after Match Odds / Bookmaker is declared. */}
                          {isOwner() && (
                          <li className={location.pathname === '/admin/settings/auto-void' ? 'mm-active' : ''}>
                            <Link to="/admin/settings/auto-void" className={`side-nav-link-ref${location.pathname === '/admin/settings/auto-void' ? ' active' : ''}`}>
                              <i className="mdi mdi-timer-off"></i> <span>Auto Void</span>
                            </Link>
                          </li>
                          )}
                          {/* Virtual Bet Limits — owner only: min/max stake per
                              virtual league (Dim / T5 XI / T10 XI / VCL / WWE). */}
                          {isOwner() && (
                          <li className={location.pathname === '/admin/settings/virtual-limits' ? 'mm-active' : ''}>
                            <Link to="/admin/settings/virtual-limits" className={`side-nav-link-ref${location.pathname === '/admin/settings/virtual-limits' ? ' active' : ''}`}>
                              <i className="mdi mdi-gamepad-variant"></i> <span>Virtual Bet Limits</span>
                            </Link>
                          </li>
                          )}
                          {/* Bet Limits — owner only: min/max stake overrides per
                              live-match market and per casino game. */}
                          {isOwner() && (
                          <li className={location.pathname === '/admin/settings/bet-limits' ? 'mm-active' : ''}>
                            <Link to="/admin/settings/bet-limits" className={`side-nav-link-ref${location.pathname === '/admin/settings/bet-limits' ? ' active' : ''}`}>
                              <i className="mdi mdi-arrow-expand-vertical"></i> <span>Bet Limits</span>
                            </Link>
                          </li>
                          )}
                          {/* Third Party Game Limit — owner only: per-bet max
                              stake for JS / third-party games, set on a
                              hierarchy node and inherited by everyone below. */}
                          {isOwner() && (
                          <li className={location.pathname === '/admin/settings/third-party-limit' ? 'mm-active' : ''}>
                            <Link to="/admin/settings/third-party-limit" className={`side-nav-link-ref${location.pathname === '/admin/settings/third-party-limit' ? ' active' : ''}`}>
                              <i className="mdi mdi-gamepad-variant-outline"></i> <span>Third Party Game Limit</span>
                            </Link>
                          </li>
                          )}
                          {/* Events submenu */}
                          {can('events') && (
                          <li id="event-tree" className={`menu-box${openSubmenus.events ? ' mm-active' : ''}`}>
                            <a href="javascript: void(0);" className="has-arrow" aria-expanded={openSubmenus.events ? 'true' : 'false'} onClick={() => toggleSubmenu('events')}>
                              <i className="bx bxs-calendar-event"></i> <span>Events</span>
                            </a>
                            <MmCollapse open={!!openSubmenus.events} className="sub-menu">
                              {sportsTree.map((sport) => {
                                const etid = String(sport.etid)
                                const sportKey = `sport-${etid}`
                                const series = sport.children || []
                                const totalMatches = series.reduce((sum, s) => sum + (s.children?.length || 0), 0)
                                /* Series keys are derived from the row's own
                                   identity, never from its position: the tree
                                   is refetched every 3s and the feed reorders
                                   competitions between ticks, so a positional
                                   key (`ser-1-6-5408272`) would change under an
                                   open accordion and slam it shut. cid is not
                                   unique or always present (a few repeat, the
                                   308 racing rows carry gmid only), so fall
                                   back to the first child's gmid and suffix
                                   the rare duplicate. */
                                const serKeys = keysFor(series).map((k) => `ser-${etid}-${k}`)
                                const sportClass = `sport${etid}`
                                /* Two different things, which we used to run off
                                   one flag:
                                     `sportOpen` — is the subtree UNFOLDED. Clicks
                                        own this, through `toggleSport`'s
                                        accordion, plus the one auto-unfold when
                                        the viewed match changes.
                                     `sportLit`  — is the row COLOURED (`li.show`
                                        drops the transparent override, so the
                                        bare `.sportN` rule paints it solid).
                                   Measured on the reference (original.MP4, 5fps,
                                   an England v Sri Lanka match open, Politics
                                   tapped):
                                     f1-f3  Cricket navy, its league and match lit,
                                            subtree open
                                     f4     Politics tan, Cricket FOLDED and its
                                            navy already washing out
                                     f5-f8  Cricket fading through grey
                                     f9+    Cricket completely plain
                                   So the viewed match keeps nothing once you
                                   click elsewhere — the colour follows the open
                                   branch, full stop (user, 2026-09-23: "Cricket
                                   wala highlight nahi hat raha hai"). Opening a
                                   match still lights its branch, because the
                                   auto-unfold below opens it. The fade is free:
                                   `#sidebar-menu ul li a` already carries
                                   `transition: all .4s` (app.css). */
                                const sportOpen = !!openSports[sportKey]
                                const sportLit = sportOpen
                                return (
                                  <li key={etid} className={sportLit ? 'show' : ''}>
                                    {/* A sport with nothing under it is a plain row, exactly as
                                        the reference writes it:
                                          <a class="sport16"><span>MotoGP</span></a>
                                        — no ` (0)`, no `has-arrow` (so no caret) and no empty
                                        <ul> after it.

                                        ONLY those go. The `sportNN` class stays (icon + hover),
                                        and so does the click handler: dropping it took the row's
                                        focus/active highlight with it, and the ask was to remove
                                        the arrow, not the effect (user, 2026-09-15 — twice now:
                                        "sirf arrow hatane ko bola tha"). With no submenu the
                                        toggle expands nothing; it just lights the row. */}
                                    <a
                                      href="javascript:void(0)"
                                      className={totalMatches ? `has-arrow ${sportClass}` : sportClass}
                                      onClick={() => toggleSport(sportKey)}
                                    >
                                      <span>{sport.name}</span>{totalMatches ? <> <span> ({totalMatches})</span></> : null}
                                    </a>
                                    {totalMatches ? (
                                    <MmCollapse open={sportOpen} className="sub-menu">
                                      {series.map((ser, serIndex) => {
                                        // `cid` is neither unique nor always
                                        // present: the feed repeats a few
                                        // (tennis 8855018, basketball 8100581,
                                        // snooker 7337119) and the racing rows
                                        // in t2 carry gmid only — 308 of them
                                        // have no cid at all. Index makes the
                                        // id unique, which this needs twice
                                        // over: as the React key, and as the
                                        // openSports toggle key (every racing
                                        // row shared `ser-10-undefined`, so
                                        // opening one opened all of them).
                                        const serKey = serKeys[serIndex]
                                        const matches = ser.children || []
                                        /* `active` paints the league in the
                                           sport's own colour at 75%
                                           (`.menu-box li a.sportN ~ ul > li >
                                           a.active`, style.css:8547+). It goes
                                           on an OPEN league — whether it opened
                                           because it holds the match being
                                           viewed, or because the operator just
                                           tapped it (user, 2026-09-23: "jo
                                           highlight wala hai wo click pe bhi
                                           active hona chahiye"). The sport row
                                           above already works that way: `li.show`
                                           drops the transparent override and the
                                           bare `.sportN` rule paints it solid on
                                           the very same tap. */
                                        // same rule as the sport above: the open
                                        // branch is the lit branch
                                        const serOpen = !!openSports[serKey]
                                        const serLit = serOpen
                                        return (
                                          <li key={serKey} className={`text-dark${serLit ? ' show' : ''}`}>
                                            <a href="javascript:void(0)" className={`has-arrow${serLit ? ' active' : ''}`} onClick={() => toggleSport(serKey)}>
                                              <span>{ser.name}</span> <span> ({matches.length})</span>
                                            </a>
                                            <MmCollapse open={serOpen} className="sub-menu">
                                              {matches.map((match) => {
                                                const gmid = String(match.gmid)
                                                const basePath = `/admin/game-details/${etid}/${gmid}`
                                                return (
                                                  /* The open match's row DOES light up — the user's
                                                     own DOM dump of the reference (2026-09-23) has
                                                     all three levels:
                                                       li.show            > a.has-arrow.sport65   (sport, solid colour)
                                                       li.text-dark.show  > a.has-arrow.active    (league, _75)
                                                       li.text-dark       > a.side-nav-link-ref.router-link-exact-active.router-link-active.active
                                                                                                  (match, _50)
                                                     I briefly removed this after reading a video
                                                     frame where the open match happened to be out of
                                                     the sampled strip. The DOM settles it. */
                                                  <li key={gmid} className="text-dark">
                                                    <Link to={basePath} className={`side-nav-link-ref${gmid === activeGmid ? ' active' : ''}`}>
                                                      <span>{match.name}</span>
                                                    </Link>
                                                  </li>
                                                )
                                              })}
                                            </MmCollapse>
                                          </li>
                                        )
                                      })}
                                    </MmCollapse>
                                    ) : null}
                                  </li>
                                )
                              })}
                            </MmCollapse>
                          </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="main-content">
          <div className="page-content">
            {(rulesLoading || myMarketLoading) && <LoaderOverlay />}
            <Outlet />
          </div>
          <footer className="footer">
            <div className="container-fluid">
              <div className="row">
                <div className="col-sm-6">2026</div>
                <div className="col-sm-6">
                  <div className="text-sm-right d-none d-sm-block"></div>
                </div>
              </div>
            </div>
          </footer>
        </div>
        {/* Reference has this empty trailing div inside layout-wrapper */}
        <div></div>
      </div>

      {/* Rules Modal */}
      {showRulesModal && <RulesModal onClose={() => { rulesAutoOpenRef.current = false; setShowRulesModal(false) }} />}
      <MyMarketModal title="Market Analysis" show={myMarket.show} analysis={myMarket.analysis} matches={myMarket.matches} onHide={() => setMyMarket({ show: false, analysis: null, matches: [] })} />

      {/* Change Password Modal */}
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}

      {/* Owner Add Balance Modal — success pe header balance turant refresh */}
      {showAddBalance && isOwner() && <OwnerAddBalanceModal onClose={() => setShowAddBalance(false)} onSuccess={() => refreshProfile()} />}
    </>
  )
}

export default Layout
